import React from "react";
import { commitLocalUpdate, commitMutation, fetchQuery, graphql, readInlineData, useFragment } from "react-relay";
import TextAreaAutoSize from "react-textarea-autosize";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faArrowRight,
	faClone,
	faMagnifyingGlassPlus,
	faPlusCircle,
	faThumbsDown,
	faThumbsUp,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faAngleRight,
	faAngleUp,
	faCalendar,
	faInfoCircle as faInfoCircleRegular,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faThumbsDown as fasThumbsDown,
	faThumbsUp as fasThumbsUp,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import cx from "classnames";
import Markdown from "markdown-to-jsx";
import invariant from "tiny-invariant";

import { AppContext } from "~/common";
import { useID } from "~/lib/hooks";
import { convertToGlobalId, useMutationPromise } from "~/lib/relayUtil";
import { getFirstUserFacingError } from "~/lib/utils";
import { VirtualKeyboardDetector } from "~/lib/virtualKeyboard";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
// eslint-disable-next-line max-len
import { openTranscriptModal } from "~/meetingRecorder/TranscriptModal";
import { copyToClipboard, useAnalyticsTrack } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { Button, toast, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import { useAskFellowShowThinkingInfoIcon } from "../../featureFlag";
import ContextPill from "./AskFellowContext";
import {
	askFellowSearchQueryEmitter,
	checkSentenceIsQuestion,
	useAskCopilotSendMessageViaConversation,
} from "./AskFellowHooks";
import { ReactComponent as ScrollToBottomIcon } from "./scroll-to-bottom.svg";
import { ReactComponent as SendButtonActive } from "./send-button-active.svg";
import { ReactComponent as SendButtonInactive } from "./send-button-inactive.svg";
import { ReactComponent as StopGeneratingButton } from "./stop-generation-button.svg";
import useAskFellowTypingAnimation from "./useAskFellowTypingAnimation";

import type { AskCopilotMessageRole } from "~/__generated__/AskCopilotPanelInner_conversation.graphql";
import type { AskFellowPanelConversationsQuery$data } from "~/__generated__/AskFellowPanelConversationsQuery.graphql";
import type { AskFellowPanelInner_ChatMessage_conversation$key } from "~/__generated__/AskFellowPanelInner_ChatMessage_conversation.graphql";
import type { AskFellowPanelInner_ChatMessage_message$key } from "~/__generated__/AskFellowPanelInner_ChatMessage_message.graphql";
import type {
	AskFellowPanelInner_groupToolUsesWithResponse_message$data,
	AskFellowPanelInner_groupToolUsesWithResponse_message$key,
} from "~/__generated__/AskFellowPanelInner_groupToolUsesWithResponse_message.graphql";
import type { AskFellowPanelInner_RatingButtons_message$key } from "~/__generated__/AskFellowPanelInner_RatingButtons_message.graphql";
import type { AskFellowPanelInner_ToolUseAndResult_result$key } from "~/__generated__/AskFellowPanelInner_ToolUseAndResult_result.graphql";
import type { AskFellowPanelInner_ToolUseAndResult_use$key } from "~/__generated__/AskFellowPanelInner_ToolUseAndResult_use.graphql";
import type { AskFellowPanelInner_useAskCopilotSendMessage_note$key } from "~/__generated__/AskFellowPanelInner_useAskCopilotSendMessage_note.graphql";
import type { AskFellowPanelInnerAddQAndAToNoteMutation } from "~/__generated__/AskFellowPanelInnerAddQAndAToNoteMutation.graphql";
import type { AskFellowPanelInnerConversationListener_conversation$key } from "~/__generated__/AskFellowPanelInnerConversationListener_conversation.graphql";
import type { AskFellowPanelInnerRateMessageMutation } from "~/__generated__/AskFellowPanelInnerRateMessageMutation.graphql";
import type { AskFellowPanelInnerSendMessageViaNoteMutation } from "~/__generated__/AskFellowPanelInnerSendMessageViaNoteMutation.graphql";
import type { AskFellowPanelInnerStopGeneratingMutation } from "~/__generated__/AskFellowPanelInnerStopGeneratingMutation.graphql";
import type { BillingRendererSubscriptionQuery as QueryType } from "~/__generated__/BillingRendererSubscriptionQuery.graphql";

import styles from "./AskFellowPanel.module.css";

export type AskCopilotConversation = Extract<
	NonNullable<AskFellowPanelConversationsQuery$data["viewer"]["conversation"]>,
	{ __typename: "AskCopilotConversation" }
>;

const GroupToolUsesWithResponseMessageFragment = graphql`
	fragment AskFellowPanelInner_groupToolUsesWithResponse_message on AskCopilotMessage @inline {
		id
		role
		content
		contentForDisplay
		contentJson
	}
`;

function parseAIResponseAsXML(text: string): Document {
	return new DOMParser().parseFromString(text, "text/xml");
}

function addQAndAToNote(assistantMessageId: string) {
	commitMutation<AskFellowPanelInnerAddQAndAToNoteMutation>(environment, {
		mutation: graphql`
			mutation AskFellowPanelInnerAddQAndAToNoteMutation($messageId: ID!) {
				askCopilotAddQAndAToNote(messageId: $messageId) {
					message {
						id
					}
				}
			}
		`,
		variables: { messageId: assistantMessageId },
		onCompleted(response, errors) {
			if (errors?.length || !response.askCopilotAddQAndAToNote) {
				toast.error("Failed to add response to note, please try again later");
			} else {
				toast.success("Response successfully added to note");
			}
		},
	});
}

interface TextMessageContent {
	type: "text";
	text: string;
}
interface ToolUseMessageContent {
	type: "tool_use";
	id: string;
	name: string;
	input: unknown;
}
interface ToolResultMessageContent {
	type: "tool_result";
	tool_use_id: string;
	content: string;
	is_error?: boolean;
}
type MessageContentItem = TextMessageContent | ToolUseMessageContent | ToolResultMessageContent;

function isTextMessageContent(content: unknown): content is TextMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "text" &&
		"text" in content &&
		typeof content.text === "string"
	);
}

function isToolUseMessageContent(content: unknown): content is ToolUseMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "tool_use" &&
		"id" in content &&
		typeof content.id === "string" &&
		"name" in content &&
		typeof content.name === "string" &&
		"input" in content
	);
}

function isToolResultMessageContent(content: unknown): content is ToolResultMessageContent {
	return (
		typeof content === "object" &&
		!!content &&
		"type" in content &&
		content.type === "tool_result" &&
		"tool_use_id" in content &&
		typeof content.tool_use_id === "string" &&
		"content" in content &&
		typeof content.content === "string"
	);
}

function* iterMessageContent(content: unknown): Generator<MessageContentItem> {
	if (!Array.isArray(content)) return;
	for (const item of content) {
		if (isTextMessageContent(item) || isToolUseMessageContent(item) || isToolResultMessageContent(item)) {
			yield item;
		}
	}
}

function isToolUseOrResultMessage(message: { contentJson: unknown }) {
	for (const item of iterMessageContent(message.contentJson)) {
		if (item.type === "tool_use" || item.type === "tool_result") return true;
	}
	return false;
}

interface TagInfo {
	start: number;
	end: number;
	tagName: string;
	isClosing: boolean;
	isComplete: boolean;
}

export type SearchMeetingsToolInput = {
	from_date?: string;
	to_date?: string;
	meeting_title?: string;
	participant_email_domains?: string[];
	participant_full_names?: string[];
	meeting_note?: string;
	meeting_note_summary?: string;
	meeting_transcript?: string;
};

export type GetActionItemsToolInput = {
	from_date?: string;
	to_date?: string;
	topic?: string;
};

/**
 * Parses "XML" bit by bit and emits content at the top level and within "result" tags.
 */
class StreamingAIResponseParser {
	path: string[] = [];
	buffer = "";
	readonly selections: readonly string[][];

	constructor(...selections: readonly string[][]) {
		this.selections = selections;
	}

	*feed(chunk: string): Generator<string> {
		const text = this.buffer + chunk;
		this.buffer = "";
		yield* this.parseContentOrTag(text);
	}

	private shouldEmit(): boolean {
		return this.selections.some(
			selection => selection.length === this.path.length && selection.every((part, i) => part === this.path[i]),
		);
	}

	private *maybeEmit(text: string): Generator<string> {
		if (this.shouldEmit()) yield text;
	}

	/**
	 * Matches a tag that might not be complete yet, for example:
	 * - <result> or </result>
	 * - <re or </r
	 * - < or </
	 * If the tag isn't complete then it must be followed by the end of the
	 * input, otherwise we don't count it as a tag. This should allow the LLM to
	 * write things like "a < b" without us trying to parse that as XML.
	 */
	private static TAG_RE = /<(\/?)(?:([a-zA-Z][\w-]*)(>|$)|$)/;

	private findTag(text: string): TagInfo | null {
		const match = StreamingAIResponseParser.TAG_RE.exec(text);
		if (!match) return null;
		const [fullMatch, closingSlash, tagName, closeBracket] = match;
		const start = match.index;
		const end = start + fullMatch.length;
		return {
			start,
			end,
			tagName: tagName ?? "",
			isClosing: !!closingSlash,
			isComplete: !!tagName && !!closeBracket,
		};
	}

	private *parseContentOrTag(text: string): Generator<string> {
		const tag = this.findTag(text);
		if (tag) {
			yield* this.parseContentWithTag(tag, text);
		} else {
			yield* this.maybeEmit(text);
		}
	}

	private *parseContentWithTag(tag: TagInfo, text: string): Generator<string> {
		// Emit any text before the open bracket
		yield* this.maybeEmit(text.slice(0, tag.start));

		if (!tag.isComplete) {
			// The tag isn't done yet, so save the state for continuing later when we
			// get the next chunk of text.
			this.buffer = text.slice(tag.start);
			return;
		}

		if (tag.isClosing) {
			// Try to tolerate missing closing tags
			const tagIndex = this.path.lastIndexOf(tag.tagName);
			if (tagIndex !== -1) this.path = this.path.slice(0, tagIndex);
		} else {
			this.path.push(tag.tagName);
		}

		// Parse any given text that comes after the tag
		let rest = text.slice(tag.end);
		// Trim leading newlines
		for (let i = 0; i < rest.length; i += 1) {
			if (rest[i] !== "\n") {
				rest = rest.slice(i);
				break;
			}
		}
		if (rest) yield* this.parseContentOrTag(rest);
	}
}

function RatingButtons({ message: messageKey }: { message: AskFellowPanelInner_RatingButtons_message$key }) {
	const message = useFragment(
		graphql`
			fragment AskFellowPanelInner_RatingButtons_message on AskCopilotMessage {
				id
				userRating
			}
		`,
		messageKey,
	);

	const [rateMessage, isInFlight] = useMutationPromise<AskFellowPanelInnerRateMessageMutation>(graphql`
		mutation AskFellowPanelInnerRateMessageMutation($messageId: ID!, $rating: AskCopilotMessageRating!)
		@raw_response_type {
			askCopilotRateAiResponse(messageId: $messageId, rating: $rating) {
				message {
					userRating
				}
			}
		}
	`);

	const onThumbsUpClick = React.useCallback(() => {
		if (isInFlight) return;
		rateMessage({
			variables: { messageId: message.id, rating: "POSITIVE" },
			optimisticResponse: {
				askCopilotRateAiResponse: {
					message: {
						id: message.id,
						userRating: "POSITIVE",
					},
				},
			},
		});
	}, [isInFlight, rateMessage, message]);

	const onThumbsDownClick = React.useCallback(() => {
		if (isInFlight) return;
		rateMessage({
			variables: { messageId: message.id, rating: "NEGATIVE" },
			optimisticResponse: {
				askCopilotRateAiResponse: {
					message: {
						id: message.id,
						userRating: "NEGATIVE",
					},
				},
			},
		});
	}, [isInFlight, rateMessage, message]);

	if (message.userRating === "POSITIVE") {
		return <IconButton className={cx(styles.lastAIMessageFooterAction, styles.disabled)} icon={fasThumbsUp} />;
	} else if (message.userRating === "NEGATIVE") {
		return (
			<IconButton
				className={cx(styles.lastAIMessageFooterAction, styles.disabled)}
				icon={fasThumbsDown}
				flip="horizontal"
			/>
		);
	}

	return (
		<>
			<IconButton className={styles.lastAIMessageFooterAction} icon={faThumbsUp} onClick={onThumbsUpClick} />
			<IconButton
				className={styles.lastAIMessageFooterAction}
				icon={faThumbsDown}
				onClick={onThumbsDownClick}
				flip="horizontal"
			/>
		</>
	);
}

// Format seconds as MM:SS
function formatTimestamp(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function MeetingReferenceTooltip({
	title,
	timestamp,
	date,
	noteId,
	meetingRecordingId,
}: {
	title: string;
	timestamp?: number;
	date?: string;
	noteId: string;
	meetingRecordingId?: string;
}) {
	const formattedTimestamp = timestamp !== undefined ? formatTimestamp(timestamp) : undefined;

	const handleRecapClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (noteId) {
			openTranscriptModal({
				noteId,
				recordingId: meetingRecordingId,
				timestamp,
			});
		}
	};

	return (
		<div className={styles.meetingReferenceTooltip}>
			<div className={styles.meetingReferenceTooltipTitle}>
				<span>{title}</span>
				{formattedTimestamp && <span className={styles.meetingReferenceTooltipText}>[{formattedTimestamp}]</span>}
			</div>

			{date && <div className={styles.meetingReferenceTooltipText}>{date}</div>}

			<a
				href="#"
				className={cx(styles.meetingReferenceTooltipLink, styles.meetingReferenceTooltipText)}
				onClick={handleRecapClick}
			>
				View meeting recap <FontAwesomeIcon icon={faArrowRight} className={styles.meetingReferenceTooltipArrow} />
			</a>
		</div>
	);
}

function CustomLink({ href, children }: { href: string; children: React.ReactNode }) {
	const isEmpty = Array.isArray(children) && children.length === 0;

	const tooltipContent = React.useMemo(() => {
		if (href && href.startsWith("/note/")) {
			const url = new URL(href, window.location.origin);
			const noteId = url.pathname.split("/note/")[1] ?? "";
			if (noteId === "") return null;
			const meetingRecordingId = url.searchParams.get("meeting_recording_id") ?? undefined;
			const meetingTitle = url.searchParams.get("title")
				? decodeURIComponent(url.searchParams.get("title") || "")
				: "Meeting";
			const meetingStartTime = url.searchParams.get("start_time");
			const quoteStartTimestamp = url.searchParams.get("t");
			const parsedQuoteStartTimestamp = quoteStartTimestamp ? parseFloat(quoteStartTimestamp) : undefined;

			// Convert IDs to GraphQL encoded values if needed
			const processedNoteId = convertToGlobalId(noteId, "Note") ?? noteId;
			const processedMeetingRecordingId = convertToGlobalId(meetingRecordingId, "MeetingRecording");

			let formattedDate: string | undefined;
			if (meetingStartTime) {
				const date = new Date(meetingStartTime);
				if (!isNaN(date.getTime())) {
					formattedDate = formatMeetingDate(date);
				}
			}

			return (
				<MeetingReferenceTooltip
					title={meetingTitle}
					timestamp={parsedQuoteStartTimestamp}
					date={formattedDate}
					noteId={processedNoteId}
					meetingRecordingId={processedMeetingRecordingId}
				/>
			);
		}
		return null;
	}, [href]);

	// If the link is empty, we don't want to render it at all
	if (!href || !href.length) {
		return null;
	}

	// If it's an empty link and we have a tooltip to show, render the document icon
	if (isEmpty && tooltipContent) {
		return (
			<Popover.Root>
				<Popover.Trigger asChild>
					<span className={styles.emptyMeetingReferenceLink}>
						<FontAwesomeIcon icon={faMagnifyingGlassPlus} className={styles.meetingReferenceLinkIcon} />
					</span>
				</Popover.Trigger>
				<Popover.Portal>
					<Popover.Content className={styles.meetingReferencePopoverContent} sideOffset={5} side="right">
						{tooltipContent}
						<Popover.Arrow className={styles.meetingReferencePopoverArrow} />
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
		);
	}

	// Otherwise render span with children as fallback
	return <span>{children}</span>;
}

const MARKDOWN_OPTIONS: React.ComponentProps<typeof Markdown>["options"] = {
	disableParsingRawHTML: true,
	overrides: {
		a: CustomLink,
	},
};

function Reference({ reference }: { reference: Reference }) {
	const onClick = React.useCallback(() => {
		if (reference.meetingNoteId) {
			openTranscriptModal({ noteId: reference.meetingNoteId.toString() });
		}
	}, [reference.meetingNoteId]);

	return (
		<div className={styles.meetingTitleWrapper} onClick={onClick}>
			<FontAwesomeIcon icon={faCalendar} className={styles.calendarIcon} />
			<span className={styles.referenceMeetingTitle}>{reference.meetingTitle}</span>
			{reference.meetingStartTime && (
				<>
					<span className={styles.titleSeparator}> | </span>
					<span className={styles.referenceMeetingDate}>{formatMeetingDate(reference.meetingStartTime)}</span>
				</>
			)}
		</div>
	);
}

// Helper function to format date as "Yesterday", "Today", or actual date
function formatMeetingDate(date: Date): string {
	const today = new Date();
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	if (date.toDateString() === today.toDateString()) {
		return "Today";
	} else if (date.toDateString() === yesterday.toDateString()) {
		return "Yesterday";
	} else {
		return meetingDateFormatter.format(date);
	}
}

// Add parsing function for references
type Reference = {
	meetingId: string;
	meetingTitle: string;
	meetingStartTime: Date | null;
	meetingNoteId: string;
	text: string;
};

export function parseReferences(content: string): Reference[] {
	// If content doesn't contain references tag, return empty array
	if (!content.includes("<references>")) return [];

	try {
		// We need to wrap the content in a root element for proper XML parsing
		// First extract just the references section to avoid parsing issues with the rest of the content
		const referencesMatch = /<references>([\s\S]*?)<\/references>/.exec(content);
		if (!referencesMatch || !referencesMatch[1]) return [];

		const referencesContent = referencesMatch[1];
		const doc = parseAIResponseAsXML(`<references>${referencesContent}</references>`);
		const references: Reference[] = [];

		// Query all reference elements
		const referenceNodes = doc.querySelectorAll("reference");

		// Extract meeting_id and text from each reference
		for (const referenceNode of referenceNodes) {
			const meetingIdNode = referenceNode.querySelector("meeting_id");
			const meetingTitleNode = referenceNode.querySelector("meeting_title");
			const meetingStartTimeNode = referenceNode.querySelector("meeting_start_time");
			const meetingNoteIdNode = referenceNode.querySelector("meeting_note_id");
			const textNode = referenceNode.querySelector("text");
			const meetingStartTime = meetingStartTimeNode?.textContent ? new Date(meetingStartTimeNode.textContent) : null;
			const isInvalidDate = meetingStartTime && isNaN(meetingStartTime.getTime());

			references.push({
				meetingId: meetingIdNode?.textContent || "",
				meetingTitle: meetingTitleNode?.textContent || "",
				meetingStartTime: isInvalidDate ? null : meetingStartTime,
				meetingNoteId: convertToGlobalId(meetingNoteIdNode?.textContent || "", "Note") ?? "",
				text: textNode?.textContent || "",
			});
		}

		return references;
	} catch (error) {
		return [];
	}
}

// Update ReferencesFooter component
function ReferencesFooter({ content }: { content: string }) {
	const [expanded, setExpanded] = React.useState(false);
	const references = React.useMemo(() => parseReferences(content), [content]);
	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const toggleExpanded = React.useCallback(() => {
		setExpanded(!expanded);
		if (!expanded) {
			dispatchAnalyticsTrack("[Ask Copilot] Viewed sources", {
				// eslint-disable-next-line camelcase
				sources_count: references.length,
			});
		}
	}, [expanded, references.length, dispatchAnalyticsTrack]);

	if (references.length === 0) return null;

	// We can have multiple references/source quoting different parts of a note,
	// but since in the UI we just link to the note we should show a single reference per note
	const referencesUniqueByNoteId = references.filter(
		(reference, index, self) => index === self.findIndex(t => t.meetingNoteId === reference.meetingNoteId),
	);

	return (
		<div className={styles.referencesFooter}>
			<button className={styles.showReasoningButton} onClick={toggleExpanded}>
				<span className={styles.showSourcesText}>Show sources</span>
				<FontAwesomeIcon icon={expanded ? faAngleUp : faAngleRight} className={styles.reasoningIcon} />
			</button>

			{expanded && (
				<div className={styles.referencesContainer}>
					<div className={styles.referencesList}>
						{referencesUniqueByNoteId.map((reference, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<div key={`reference-${index}`} className={styles.referenceItem}>
								<Reference reference={reference} />
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

const ChatMessage = ({
	conversation: conversationKey,
	message: messageKey,
	hideButtons,
}: {
	conversation: AskFellowPanelInner_ChatMessage_conversation$key;
	message: AskFellowPanelInner_ChatMessage_message$key | null;
	hideButtons?: boolean;
}) => {
	const conversation = useFragment(
		graphql`
			fragment AskFellowPanelInner_ChatMessage_conversation on AskCopilotConversation {
				note {
					id
					__typename
				}
			}
		`,
		conversationKey,
	);

	const [renderAllowed, setRenderAllowed] = React.useState(false);
	const [parser] = React.useState(() => new StreamingAIResponseParser([], ["result"]));
	const [parsedContent, setParsedContent] = React.useState("");
	const lastFedIndex = React.useRef(0);
	const dispatchAnalyticsTrack = useAnalyticsTrack();
	const markdownContentRef = React.useRef<HTMLDivElement>(null);

	const message = useFragment(
		graphql`
			fragment AskFellowPanelInner_ChatMessage_message on AskCopilotMessage {
				id
				role
				content
				contentJson
				contentForDisplay
				pending
				isResponse
				...AskFellowPanelInner_ToolUseAndResult_use
				...AskFellowPanelInner_RatingButtons_message
			}
		`,
		messageKey,
	);

	// This effect initializes our state on mount and when message changes
	React.useEffect(() => {
		if (message && message.role === "USER") {
			setParsedContent(message.content);
		}
		setRenderAllowed(true);
	}, [message]);

	// Effect to process streamed message content
	React.useEffect(() => {
		if (!message || message.role !== "ASSISTANT") return;
		if (lastFedIndex.current >= message.content.length) return;

		let newContent = "";
		for (const chunk of parser.feed(message.content.slice(lastFedIndex.current))) {
			newContent += chunk;
		}
		lastFedIndex.current = message.content.length;
		setParsedContent(existing => existing + newContent);
	}, [message, parser]);

	const getCleanedHtmlContent = React.useCallback(() => {
		if (!markdownContentRef.current) return null;

		// Clone the DOM element to avoid modifying the original
		const clonedElement = markdownContentRef.current.cloneNode(true) as HTMLElement;

		// Remove all magnifying glass icons and their container spans
		const magnifyingGlassIcons = clonedElement.querySelectorAll('svg[data-icon="magnifying-glass-plus"]');
		magnifyingGlassIcons.forEach(icon => {
			// Remove the entire span that contains the magnifying glass icon
			const parentSpan = icon.closest("span");
			if (parentSpan) {
				parentSpan.remove();
			} else {
				icon.remove();
			}
		});

		// Also remove any spans with the emptyMeetingReferenceLink class that might contain the icon
		const emptyRefLinks = clonedElement.querySelectorAll('span[class*="emptyMeetingReferenceLink"]');
		emptyRefLinks.forEach(span => span.remove());

		return clonedElement.innerHTML;
	}, []);

	const onCopyClick = React.useCallback(() => {
		if (!message) return;

		let contentToCopy: string;

		// For assistant messages, try to get cleaned HTML content first
		if (message.role === "ASSISTANT" && markdownContentRef.current) {
			const htmlContent = getCleanedHtmlContent();
			if (htmlContent) {
				// Create a temporary div to convert HTML to plain text while preserving formatting
				const tempDiv = document.createElement("div");
				tempDiv.innerHTML = htmlContent;

				// Copy as HTML to clipboard to maintain formatting
				const clipboardItem = new ClipboardItem({
					"text/html": new Blob([htmlContent], { type: "text/html" }),
					"text/plain": new Blob([tempDiv.innerText || tempDiv.textContent || ""], { type: "text/plain" }),
				});

				navigator.clipboard
					.write([clipboardItem])
					.then(() => {
						toast.success("Copied to clipboard");
						dispatchAnalyticsTrack("[Ask Copilot] Copied response to clipboard", {
							// eslint-disable-next-line camelcase
							ai_message_id: message.id,
						});
					})
					.catch(() => {
						// Fallback to plain text if HTML copy fails
						contentToCopy = tempDiv.innerText || tempDiv.textContent || (message.contentForDisplay ?? parsedContent);
						copyToClipboard(contentToCopy);
						dispatchAnalyticsTrack("[Ask Copilot] Copied response to clipboard", {
							// eslint-disable-next-line camelcase
							ai_message_id: message.id,
						});
					});
				return;
			}
		}

		// Fallback to original behavior for user messages or if HTML extraction fails
		contentToCopy = message.contentForDisplay ?? parsedContent;
		copyToClipboard(contentToCopy);
		dispatchAnalyticsTrack("[Ask Copilot] Copied response to clipboard", {
			// eslint-disable-next-line camelcase
			ai_message_id: message.id,
		});
	}, [message, parsedContent, dispatchAnalyticsTrack, getCleanedHtmlContent]);

	const onAddToNoteClick = React.useCallback(() => {
		if (!message || message.role !== "ASSISTANT") return;
		if (conversation.note?.__typename !== "Note") return;

		addQAndAToNote(message.id);
		dispatchAnalyticsTrack("[Ask Copilot] Added Q&A to note", {
			// eslint-disable-next-line camelcase
			ai_message_id: message.id,
		});
	}, [conversation, message, dispatchAnalyticsTrack]);

	const showThinkingInfoIcon = useAskFellowShowThinkingInfoIcon();
	const thinkingText = React.useMemo(() => {
		if (!message || message.role !== "ASSISTANT") return null;
		const text = message.content;
		if (!text.includes("<thinking>") && !text.includes("<search_quality_reflection>")) return null;
		const doc = parseAIResponseAsXML(`<xml>${text}</xml>`);
		const thinkingNode = doc.querySelector("thinking");
		const searchQualityReflectionNode = doc.querySelector("search_quality_reflection");
		if (thinkingNode?.textContent) return thinkingNode.textContent;
		if (searchQualityReflectionNode?.textContent) return searchQualityReflectionNode.textContent;
		return null;
	}, [message]);

	const thinkingNode =
		showThinkingInfoIcon && thinkingText?.trim() ? (
			<Tooltip content={thinkingText.trim()} className={styles.thinkingTooltip}>
				<FontAwesomeIcon
					icon={faInfoCircleRegular}
					className={cx(styles.lastAIMessageFooterAction, styles.thinkingAssistantInfoIcon)}
				/>
			</Tooltip>
		) : null;

	if (!renderAllowed) return null;

	if (!message) return null;

	// Content processing
	const contentForDisplay = ((message.contentForDisplay ?? parsedContent) || "")
		.trim()
		.replace(/\[([^\]]*)$/, "") // Remove any incomplete markdown links at the end like [part of link
		.replace(/\[([^\]]*)]\(([^)]*)$/, ""); // Remove incomplete markdown links like [part of link](part of link

	if (message.role === "ASSISTANT" && !contentForDisplay) return null;

	// Set up render variables
	const isAboutAllMeetings = conversation.note == null;
	const messageContentNode =
		message.role === "ASSISTANT" ? (
			<Markdown options={MARKDOWN_OPTIONS}>{contentForDisplay}</Markdown>
		) : (
			contentForDisplay
		);

	return (
		<div
			className={cx(styles.message, {
				[styles.messageUser]: message.role === "USER",
			})}
		>
			<div className={styles.messageContentContainer}>
				<div
					ref={message.role === "ASSISTANT" ? markdownContentRef : undefined}
					className={cx(styles.messageContent, {
						[styles.messageContentAssistant]: message.role === "ASSISTANT",
						[styles.messageContentUser]: message.role === "USER",
					})}
				>
					{messageContentNode}
				</div>

				{message.role === "ASSISTANT" && !message.pending && (
					<ReferencesFooter content={message.content || parsedContent} />
				)}

				{!hideButtons && message.role === "ASSISTANT" && !message.pending && message.isResponse && (
					<div className={styles.lastAIMessageFooter}>
						<div className={styles.lastAIMessageFooterActions}>
							{!isAboutAllMeetings && conversation.note?.__typename === "Note" && (
								<Tooltip content="Add to note">
									<IconButton
										className={styles.lastAIMessageFooterAction}
										icon={faPlusCircle}
										onClick={onAddToNoteClick}
									/>
								</Tooltip>
							)}
							<Tooltip content="Copy">
								<IconButton className={styles.lastAIMessageFooterAction} icon={faClone} onClick={onCopyClick} />
							</Tooltip>
							{thinkingNode}
							<RatingButtons message={message} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export function useStopGenerating(messageId: string | null | undefined, onComplete?: () => void) {
	const stopGenerating = React.useCallback(() => {
		if (!messageId) return;

		commitMutation<AskFellowPanelInnerStopGeneratingMutation>(environment, {
			mutation: graphql`
				mutation AskFellowPanelInnerStopGeneratingMutation($messageId: ID!) {
					askCopilotStopGenerating(messageId: $messageId) {
						conversation {
							...AskFellowPanelInnerFragment
						}
					}
				}
			`,
			variables: {
				messageId: messageId,
			},
			onCompleted: () => {
				if (onComplete) onComplete();
			},
			onError: () => {
				if (onComplete) onComplete();
			},
		});
	}, [messageId, onComplete]);

	return stopGenerating;
}

export function useAskCopilotSendMessageViaNote(noteKey: AskFellowPanelInner_useAskCopilotSendMessage_note$key) {
	const note = useFragment(
		graphql`
			fragment AskFellowPanelInner_useAskCopilotSendMessage_note on Note {
				id
			}
		`,
		noteKey,
	);

	const [commit, isInFlight] = useMutationPromise<AskFellowPanelInnerSendMessageViaNoteMutation>(graphql`
		mutation AskFellowPanelInnerSendMessageViaNoteMutation(
			$noteId: ID!
			$content: String!
			$messageId: String
			$contentForDisplay: String
		) {
			askCopilotSendNoteMessage(
				content: $content
				noteId: $noteId
				messageId: $messageId
				contentForDisplay: $contentForDisplay
			) {
				note {
					askCopilotConversation {
						...AskFellowPanelInnerFragment
					}
				}
			}
		}
	`);

	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			return commit({
				variables: {
					noteId: note.id,
					messageId,
					content,
					contentForDisplay,
				},
				onCompleted: (_result, errors) => {
					if (errors) {
						const message = getFirstUserFacingError(errors) ?? "Something went wrong; please try again later.";
						toast.error(message);
					}
				},
			});
		},
		[commit, note],
	);

	return [sendMessage, isInFlight] as const;
}

graphql`
	fragment AskFellowPanelInnerFragment on AskCopilotConversation {
		id
		title
		messages {
			id
			role
			content
			contentJson
			pending
			createdAt
			isResponse
			contentForDisplay
			...AskFellowPanelInner_groupToolUsesWithResponse_message
			...AskFellowPanelInner_ToolUseAndResult_use
			...AskFellowPanelInner_ToolUseAndResult_result
			...AskFellowPanelInner_ChatMessage_message
		}
		suggestedQuestions {
			id
			...AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion
		}
		reachedMessageLimit
	}
`;

const AskFellowPanelInnerRefreshQuery = graphql`
	query AskFellowPanelInnerRefreshQuery($conversationId: ID!) {
		node(id: $conversationId) {
			... on AskCopilotConversation {
				...AskFellowPanelInnerFragment @relay(mask: false)
			}
		}
	}
`;

type WSMessage =
	| { type: "NEW_RESPONSE_PART"; response_part: string; message_id: string }
	| { type: "RESPONSE_COMPLETE"; message_id: string }
	| {
			type: "NEW_MESSAGE";
			message: {
				id: string;
				role: AskCopilotMessageRole;
				content: string;
				content_json: unknown;
				pending: boolean;
				created_at: string;
				is_response: boolean;
			};
	  }
	| { type: "REFETCH" }
	| { type: "NEW_CONTENT_JSON"; content_json: unknown; message_id: string }
	| { type: "something else" };

// Ask Copilot can be open multiple times for the same conversation at once. In
// that case we need to only handle each websocket message once to avoid adding
// each token to the message twice. To do so, we have a registry of "store
// updaters" keyed by conversation ID, each of which has a reference count.
// When a new AskCopilotPanel is rendered for a conversation, it gets/creates
// the StoreUpdater, increments the refcount, and registers a listener for when
// a new message part is received. When that AskCopilotPanel is unmounted, it
// removes the message part listener, decrements the refcount, and deletes the
// store updater if there are no more references. See useAskCopilotConversationListener
// for that logic.
interface StoreUpdater {
	groupEmitter: GroupEmitter;
	listener: (message: WSMessage) => void;
	messagePartListeners: Set<() => void>;
	refcount: number;
}

const storeUpdaters = new Map<string, StoreUpdater>();

function handleWSMessage(wsMessage: WSMessage, conversationId: string, messagePartListeners: readonly (() => void)[]) {
	if (wsMessage.type === "NEW_RESPONSE_PART") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			messageNode.setValue(messageNode.getValue("content") + wsMessage.response_part, "content");
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "NEW_CONTENT_JSON") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			// To set a JSON value we need to use this internal relay API otherwise
			/* eslint-disable no-underscore-dangle */
			// @ts-ignore
			messageNode._mutator.setValue(messageNode._dataID, "contentJson", wsMessage.content_json);
			/* eslint-enable no-underscore-dangle */
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "RESPONSE_COMPLETE") {
		commitLocalUpdate(environment, store => {
			const messageNode = store.get(wsMessage.message_id);
			if (!messageNode) return;

			messageNode.setValue(false, "pending");
		});
		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "NEW_MESSAGE") {
		const { message: newMessageData } = wsMessage;
		commitLocalUpdate(environment, store => {
			let newMessage = store.get(newMessageData.id);
			const alreadyExisted = !!newMessage;
			if (!newMessage) newMessage = store.create(newMessageData.id, "AskCopilotMessage");
			newMessage.setValue(newMessageData.id, "id");
			newMessage.setValue(newMessageData.role, "role");
			newMessage.setValue(newMessageData.content, "content");
			// To set a JSON value we need to use this internal relay API otherwise
			/* eslint-disable no-underscore-dangle */
			// @ts-ignore
			newMessage._mutator.setValue(newMessage._dataID, "contentJson", newMessageData.content_json);
			/* eslint-enable no-underscore-dangle */
			newMessage.setValue(newMessageData.pending, "pending");
			newMessage.setValue(newMessageData.created_at, "createdAt");
			newMessage.setValue(newMessageData.is_response, "isResponse");

			// If the message was already in the relay store it should also be in the
			// conversation's messages list, so don't add it again.
			if (alreadyExisted) return;
			const conversationNode = store.get(conversationId);
			if (!conversationNode) return;
			const messages = [...(conversationNode.getLinkedRecords("messages") ?? []), newMessage];
			conversationNode.setLinkedRecords(messages, "messages");
		});

		messagePartListeners.forEach(listener => listener());
	} else if (wsMessage.type === "REFETCH") {
		refetch();
	}

	function refetch() {
		fetchQuery<QueryType>(
			environment,
			AskFellowPanelInnerRefreshQuery,
			{ conversationId: conversationId },
			{ fetchPolicy: "network-only" },
		).toPromise();
	}
}

function useAskFellowPanelConversationListener(
	conversationKey: AskFellowPanelInnerConversationListener_conversation$key | null,
	onNewMessagePart: () => void,
) {
	const conversation = useFragment(
		graphql`
			fragment AskFellowPanelInnerConversationListener_conversation on AskCopilotConversation {
				id
				groupName
			}
		`,
		conversationKey,
	);

	React.useEffect(() => {
		if (!conversation) return;

		let updater = storeUpdaters.get(conversation.id);
		if (updater) {
			updater.refcount += 1;
			updater.messagePartListeners.add(onNewMessagePart);
		} else {
			const groupEmitter = WebsocketClient.getInstance().getOrJoinGroup(conversation.groupName);
			groupEmitter.on("message", listener);

			const messagePartListeners = new Set([onNewMessagePart]);
			updater = {
				groupEmitter,
				listener,
				messagePartListeners,
				refcount: 1,
			};

			storeUpdaters.set(conversation.id, updater);

			function listener(message: WSMessage) {
				if (!conversation) return;
				handleWSMessage(message, conversation.id, [...messagePartListeners]);
			}
		}

		return () => {
			const updater = storeUpdaters.get(conversation.id);
			if (updater) {
				updater.refcount -= 1;
				if (updater.refcount === 0) {
					updater.groupEmitter.off("message", updater.listener);
					updater.groupEmitter.close();
					storeUpdaters.delete(conversation.id);
				} else {
					updater.messagePartListeners.delete(onNewMessagePart);
				}
			}
		};
	}, [conversation, onNewMessagePart]);
}

function readMessage(
	messageKey: AskFellowPanelInner_groupToolUsesWithResponse_message$key,
): AskFellowPanelInner_groupToolUsesWithResponse_message$data | null {
	if (!messageKey) return null;
	return readInlineData<AskFellowPanelInner_groupToolUsesWithResponse_message$key>(
		GroupToolUsesWithResponseMessageFragment,
		messageKey,
	);
}

function hasTextContent(messageKey: AskFellowPanelInner_groupToolUsesWithResponse_message$key) {
	const message = readMessage(messageKey);
	if (!message) return false;
	const content = message?.content || message?.contentForDisplay;
	if (!content) return false;
	const parser = new StreamingAIResponseParser([], ["result"]);
	let text = "";
	for (const chunk of parser.feed(content)) {
		text += chunk;
	}
	return !!text.trim();
}

function isToolUse(messageKey: AskFellowPanelInner_groupToolUsesWithResponse_message$key) {
	const message = readMessage(messageKey);
	if (!message) return false;
	return (
		message?.role === "ASSISTANT" &&
		[...iterMessageContent(message.contentJson)].some(content => isToolUseMessageContent(content))
	);
}

function isToolResult(messageKey: AskFellowPanelInner_groupToolUsesWithResponse_message$key) {
	const message = readMessage(messageKey);
	if (!message) return false;
	return (
		message?.role === "USER" &&
		[...iterMessageContent(message.contentJson)].some(content => isToolResultMessageContent(content))
	);
}

type GroupedToolResult =
	| {
			type: "toolUsesAndResults";
			usesAndResults: {
				use: AskFellowPanelInner_ToolUseAndResult_use$key;
				result: AskFellowPanelInner_ToolUseAndResult_result$key | undefined;
			}[];
	  }
	| { type: "message"; message: AskFellowPanelInner_groupToolUsesWithResponse_message$key };

function groupToolUsesWithResponse(
	messages: readonly AskFellowPanelInner_groupToolUsesWithResponse_message$key[],
): GroupedToolResult[] {
	const result: GroupedToolResult[] = [];
	let currentToolUsesAndResults: {
		use: AskFellowPanelInner_ToolUseAndResult_use$key;
		result: AskFellowPanelInner_ToolUseAndResult_result$key | undefined;
	}[] = [];

	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];
		if (!message) continue;

		if (isToolUse(message)) {
			i += 1; // Move to the next message in the conversation
			const toolResultMessage: AskFellowPanelInner_groupToolUsesWithResponse_message$key | undefined = messages[i];
			invariant(!toolResultMessage || isToolResult(toolResultMessage), "Expected tool result to follow tool use");

			if (hasTextContent(message)) {
				if (currentToolUsesAndResults.length) {
					result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
					currentToolUsesAndResults = [];
				}
				result.push({ type: "message", message });
			}

			currentToolUsesAndResults.push({
				use: message as unknown as AskFellowPanelInner_ToolUseAndResult_use$key,
				result: toolResultMessage as unknown as AskFellowPanelInner_ToolUseAndResult_result$key | undefined,
			});
		} else {
			if (currentToolUsesAndResults.length) {
				result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
				currentToolUsesAndResults = [];
			}
			result.push({ type: "message", message });
		}
	}

	if (currentToolUsesAndResults.length) {
		result.push({ type: "toolUsesAndResults", usesAndResults: currentToolUsesAndResults });
	}
	return result;
}

type ActionItem = {
	text: string;
};

type ActionItemsResult = {
	noteTitle: string;
	noteId: string;
	actionItems: ActionItem[];
}[];

function parseActionItemsResult(rawContent: string): ActionItemsResult {
	const doc = parseAIResponseAsXML(rawContent);

	const parsedResults = [];
	for (const meetingElement of doc.querySelectorAll("meeting")) {
		const noteTitle = meetingElement.querySelector("note_title")?.textContent;
		const noteId = meetingElement.querySelector("note_id")?.textContent;

		if (!noteTitle || !noteId) continue;

		const actionItems = [];
		for (const actionItemElement of meetingElement.querySelectorAll("action_item")) {
			const text = actionItemElement.querySelector("text")?.textContent;

			if (!text) continue;
			actionItems.push({ text });
		}

		parsedResults.push({ noteTitle, noteId, actionItems });
	}

	return parsedResults;
}

type SearchMeetingsResult = {
	meetingTitle: string;
	meetingStartTime: Date;
}[];

function parseSearchMeetingsResult(rawContent: string): SearchMeetingsResult {
	const doc = parseAIResponseAsXML(rawContent);

	const parsedResults = [];
	for (const result of doc.querySelectorAll("meeting")) {
		const meetingTitle = result.querySelector("meeting_title")?.textContent;
		const meetingStartTimeNode = result.querySelector("meeting_start_time"),
			meetingStartTime = meetingStartTimeNode?.textContent ? new Date(meetingStartTimeNode.textContent) : null;

		if (!meetingTitle || !meetingStartTime || isNaN(meetingStartTime.getTime())) continue;

		parsedResults.push({ meetingTitle, meetingStartTime });
	}

	return parsedResults;
}

function decodeHTMLEntities(text: string): string {
	const textArea = document.createElement("textarea");
	textArea.innerHTML = text;
	return textArea.value;
}

function DetailsSummary({
	summary,
	children,
	className,
	summaryClassName,
	currentlyGenerating,
}: React.PropsWithChildren<{
	summary: React.ReactNode;
	className?: string;
	summaryClassName?: string;
	currentlyGenerating?: boolean;
}>) {
	const [open, setOpen] = React.useState(false);
	const detailsRef = React.useRef<HTMLDetailsElement>(null);
	const hasChildren = React.Children.count(children) > 0;

	function onToggle(event: React.SyntheticEvent<HTMLDetailsElement>) {
		if (event.target !== detailsRef.current) return;
		setOpen(open => !open);
	}

	if (currentlyGenerating) {
		// Always open, non-expandable, but styled like the summary/children
		return (
			<div className={cx(styles.details, className)}>
				<div className={cx(styles.summary, summaryClassName)}>
					<span className={styles.summaryText}>{summary}</span>
				</div>
				{hasChildren && <div>{children}</div>}
			</div>
		);
	}

	// If there are no children, just render a paragraph with the summary text (no toggle)
	if (!hasChildren) {
		return (
			<p className={cx(styles.summary, summaryClassName, className)}>
				<span className={styles.summaryText}>{summary}</span>
			</p>
		);
	}

	// Otherwise render the full interactive details element
	return (
		<details ref={detailsRef} onToggle={onToggle} open={open} className={cx(styles.details, className)}>
			<summary className={cx(styles.summary, summaryClassName)}>
				<span className={styles.summaryText}>
					{summary}
					<FontAwesomeIcon icon={open ? faAngleUp : faAngleRight} className={styles.detailsIcon} />
				</span>
			</summary>
			{children}
		</details>
	);
}

function ToolDetails({
	summary,
	loading,
	isError,
	children,
}: React.PropsWithChildren<{ loading?: boolean; summary: React.ReactNode; isError?: boolean; number?: number }>) {
	if (isError) {
		return (
			<p className={styles.summary}>
				<span className={styles.summaryText}>{summary} - failed</span>
			</p>
		);
	}

	if (loading) {
		return (
			<p className={cx(styles.summary)}>
				<span className={styles.summaryText}>
					<div className={styles.loadingIndicatorWrapper}>
						<span className={styles.loadingIndicator}>{summary}</span>
						<span className={styles.loadingShimmerBar}></span>
					</div>
				</span>
			</p>
		);
	}

	if (!children) {
		return (
			<p className={cx(styles.summary, styles.toolUseWithoutIcon)}>
				<span className={styles.summaryText}>{summary}</span>
			</p>
		);
	}

	return <DetailsSummary summary={summary}>{children}</DetailsSummary>;
}

const meetingDateFormatter = new Intl.DateTimeFormat(undefined, {
	month: "short",
	day: "numeric",
	year: "numeric",
});

function ToolUseAndResult({
	use: useKey,
	result: resultKey,
	currentlyGenerating,
}: {
	use: AskFellowPanelInner_ToolUseAndResult_use$key;
	result: AskFellowPanelInner_ToolUseAndResult_result$key | undefined;
	currentlyGenerating?: boolean;
}) {
	const use = useFragment(
		graphql`
			fragment AskFellowPanelInner_ToolUseAndResult_use on AskCopilotMessage {
				contentJson
			}
		`,
		useKey,
	);

	const result = useFragment(
		graphql`
			fragment AskFellowPanelInner_ToolUseAndResult_result on AskCopilotMessage {
				contentJson
			}
		`,
		resultKey ?? null,
	);

	const showThinkingInfoIcon = useAskFellowShowThinkingInfoIcon();

	const toolThinking = React.useMemo(() => {
		const text = [...iterMessageContent(use.contentJson)].find(
			(content): content is TextMessageContent => content.type === "text",
		)?.text;
		if (!text) return null;
		if (!text.includes("<thinking>") && !text.includes("<search_quality_reflection>")) return text;
		const doc = parseAIResponseAsXML(`<xml>${text}</xml>`);
		const thinkingNode = doc.querySelector("thinking");
		const searchQualityReflectionNode = doc.querySelector("search_quality_reflection");
		if (thinkingNode?.textContent) return thinkingNode.textContent;
		if (searchQualityReflectionNode?.textContent) return searchQualityReflectionNode.textContent;
		return null;
	}, [use]);

	const thinkingNode = showThinkingInfoIcon && toolThinking?.trim() && (
		<Tooltip content={toolThinking.trim()} className={styles.thinkingTooltip}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.thinkingInfoIcon} />
		</Tooltip>
	);

	const toolUsed = [...iterMessageContent(use.contentJson)].find(isToolUseMessageContent);
	if (!toolUsed) return null;

	switch (toolUsed.name) {
		case "get_action_items": {
			let toolResult: ToolResultMessageContent | undefined;
			let allActionItems: string[] = [];
			let noActionItemsFound = false;

			// --- Begin new thinking formatting logic for get_action_items ---
			const input = toolUsed.input as GetActionItemsToolInput;
			const lines: React.ReactNode[] = [];

			// Date formatting
			const rawFromDate = input.from_date;
			const rawToDate = input.to_date;
			let dateLine = "";
			if (rawFromDate || rawToDate) {
				if (rawFromDate) {
					const fromStr = new Date(rawFromDate).toLocaleDateString(undefined, {
						month: "short",
						day: "2-digit",
						year: "numeric",
					});
					dateLine += `From <strong>${fromStr}</strong>`;
				}
				if (rawToDate) {
					const toStr = new Date(rawToDate).toLocaleDateString(undefined, {
						month: "short",
						day: "2-digit",
						year: "numeric",
					});
					if (rawFromDate) {
						dateLine += ` up to <strong>${toStr}</strong>`;
					} else {
						dateLine += `Up to <strong>${toStr}</strong>`;
					}
				}
				lines.push(<div key="date" dangerouslySetInnerHTML={{ __html: dateLine }} />);
			}

			// Topic
			if (input.topic) {
				lines.push(
					<div key="topic">
						Contains <strong>{input.topic}</strong>
					</div>,
				);
			}

			const thinking = lines.length > 0 ? <div className={styles.thinkingContent}>{lines}</div> : null;
			// --- End new thinking formatting logic for get_action_items ---

			if (result) {
				toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
				if (toolResult && !toolResult.is_error) {
					const results = parseActionItemsResult(toolResult.content);
					allActionItems = results.flatMap(result => result.actionItems.map(item => item.text));
					noActionItemsFound = allActionItems.length === 0;
				}
			}

			return (
				<>
					<DetailsSummary summary={<>Search Action items</>} currentlyGenerating={currentlyGenerating}>
						{thinking}
					</DetailsSummary>
					{result && toolResult && !toolResult.is_error ? (
						<DetailsSummary summary={<>Analyze Action items</>} currentlyGenerating={currentlyGenerating}>
							<div className={styles.actionItemsContainer}>
								{noActionItemsFound && <div className={styles.actionItemText}>No action items found</div>}
								{(currentlyGenerating ? allActionItems.slice(0, 10) : allActionItems).map((itemText, index) => (
									// eslint-disable-next-line react/no-array-index-key
									<div key={`action-item-text-${index}`} className={styles.actionItemText}>
										{itemText}
									</div>
								))}
								{currentlyGenerating && allActionItems.length > 10 && (
									<div className={styles.actionItemText}>more action items ...</div>
								)}
							</div>
						</DetailsSummary>
					) : (
						<ToolDetails loading summary={<>Analyzing Action Items</>} isError={toolResult?.is_error} />
					)}
					{thinkingNode}
				</>
			);
		}
		case "get_meetings_from_calendar":
		case "search_meetings": {
			let toolResult: ToolResultMessageContent | undefined;
			let results: ReturnType<typeof parseSearchMeetingsResult> = [];
			let noMeetingsFound = false;

			// --- Begin new thinking formatting logic ---
			const input = toolUsed.input as SearchMeetingsToolInput;
			const lines: React.ReactNode[] = [];

			// Date formatting
			const rawFromDate = input.from_date;
			const rawToDate = input.to_date;
			let dateLine = "";
			if (rawFromDate || rawToDate) {
				if (rawFromDate) {
					const fromStr = new Date(rawFromDate).toLocaleDateString(undefined, {
						month: "short",
						day: "2-digit",
						year: "numeric",
					});
					dateLine += `From <strong>${fromStr}</strong>`;
				}
				if (rawToDate) {
					const toStr = new Date(rawToDate).toLocaleDateString(undefined, {
						month: "short",
						day: "2-digit",
						year: "numeric",
					});
					if (rawFromDate) {
						dateLine += ` up to <strong>${toStr}</strong>`;
					} else {
						dateLine += `Up to <strong>${toStr}</strong>`;
					}
				}
				lines.push(<div key="date" dangerouslySetInnerHTML={{ __html: dateLine }} />);
			}

			// Meeting title
			if (input.meeting_title) {
				lines.push(
					<div key="title">
						Meeting title contains <strong>{input.meeting_title}</strong>
					</div>,
				);
			}

			// Mentions (deduplicated)
			const mentionFields = [input.meeting_note, input.meeting_note_summary, input.meeting_transcript];
			const mentions = Array.from(new Set(mentionFields.filter(Boolean)));
			if (mentions.length > 0) {
				lines.push(
					<div key="mentions">
						Mention of{" "}
						{mentions.map((m, i) => (
							// eslint-disable-next-line react/no-array-index-key
							<React.Fragment key={`mentions-${i}`}>
								{i > 0 && ", "}
								<strong>{m}</strong>
							</React.Fragment>
						))}
					</div>,
				);
			}

			// Participants (emails and names)
			const emails = Array.isArray(input.participant_email_domains) ? input.participant_email_domains : [];
			const names = Array.isArray(input.participant_full_names) ? input.participant_full_names : [];
			const participants = [...emails, ...names];
			if (participants.length > 0) {
				lines.push(
					<div key="participants">
						With{" "}
						{participants.map((p, i) => (
							// eslint-disable-next-line react/no-array-index-key
							<React.Fragment key={`participants-${i}`}>
								{i > 0 && ", "}
								<strong>{p}</strong>
							</React.Fragment>
						))}
					</div>,
				);
			}
			// --- End new thinking formatting logic ---

			const thinking = lines.length > 0 ? <div className={styles.thinkingContent}>{lines}</div> : null;

			if (result) {
				toolResult = [...iterMessageContent(result.contentJson)].find(isToolResultMessageContent);
				if (toolResult && !toolResult.is_error) {
					results = parseSearchMeetingsResult(toolResult.content);
					noMeetingsFound = results.length === 0;
				}
			}

			return (
				<>
					<DetailsSummary summary={<>Search meetings</>} currentlyGenerating={currentlyGenerating}>
						{thinking}
					</DetailsSummary>
					{result && toolResult && !toolResult.is_error ? (
						<DetailsSummary summary={<>Analyze meetings</>} currentlyGenerating={currentlyGenerating}>
							<div className={styles.meetingsContainer}>
								{noMeetingsFound && (
									<div className={styles.meetingItem}>
										<span className={styles.meetingTitle}>No meetings found</span>
									</div>
								)}
								{(currentlyGenerating ? results.slice(0, 10) : results).map((meeting, index) => (
									// eslint-disable-next-line react/no-array-index-key
									<div key={`meeting-${index}`} className={styles.meetingItem}>
										<span className={styles.meetingTitle}>
											{decodeHTMLEntities(meeting.meetingTitle)} |{" "}
											{meetingDateFormatter.format(meeting.meetingStartTime)}
										</span>
									</div>
								))}
								{currentlyGenerating && results.length > 10 && (
									<div className={styles.meetingItem}>
										<span className={styles.meetingTitle}>more meetings ...</span>
									</div>
								)}
							</div>
						</DetailsSummary>
					) : (
						<ToolDetails loading summary={<>Analyzing meetings</>} isError={toolResult?.is_error} />
					)}
					{thinkingNode}
				</>
			);
		}
		case "get_meeting_participants": {
			return (
				<>
					<DetailsSummary summary={<>Finding meeting participants</>} currentlyGenerating={currentlyGenerating} />
					{thinkingNode}
				</>
			);
		}
		case "get_meeting_summary": {
			return (
				<>
					<DetailsSummary summary={<>Finding meeting summary</>} currentlyGenerating={currentlyGenerating} />
					{thinkingNode}
				</>
			);
		}
		case "get_meeting_transcript": {
			return (
				<>
					<DetailsSummary summary={<>Finding meeting transcript</>} currentlyGenerating={currentlyGenerating} />
					{thinkingNode}
				</>
			);
		}
		default:
			return null;
	}
}

function ToolsUsed({
	tools,
	currentlyGenerating,
}: {
	tools: readonly {
		use: AskFellowPanelInner_ToolUseAndResult_use$key;
		result: AskFellowPanelInner_ToolUseAndResult_result$key | undefined;
	}[];
	currentlyGenerating?: boolean;
}) {
	const [expanded, setExpanded] = React.useState(currentlyGenerating ?? false);
	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const handleExpand = React.useCallback(() => {
		setExpanded(true);
		dispatchAnalyticsTrack("[Ask Copilot] Clicked to show reasoning");
	}, [dispatchAnalyticsTrack]);

	if (!tools.length) return null;

	const latestTool = tools[tools.length - 1];
	const latestToolHasResult = latestTool.result !== undefined;

	return (
		<div className={styles.toolsUsedContainer}>
			{expanded ? (
				<div className={styles.reasoningContainerWithBorder}>
					<button className={styles.showReasoningButton} onClick={() => setExpanded(false)}>
						<span>Reasoning</span>
						<FontAwesomeIcon icon={faAngleUp} className={styles.reasoningIcon} />
					</button>
					<ul className={styles.reasoningSections}>
						{tools.map(({ use, result }, i) => (
							// eslint-disable-next-line react/no-array-index-key
							<li key={i} className={styles.reasoningSectionItem}>
								<ToolUseAndResult currentlyGenerating={currentlyGenerating} use={use} result={result} />
							</li>
						))}
					</ul>
					{currentlyGenerating && latestToolHasResult && (
						<p className={cx(styles.summary)}>
							<span className={styles.summaryText}>
								<div className={styles.loadingIndicatorWrapper}>
									<span className={styles.loadingIndicator}>Thinking...</span>
									<span className={styles.loadingShimmerBar}></span>
								</div>
							</span>
						</p>
					)}
				</div>
			) : (
				<div className={styles.reasoningContainer}>
					<button className={styles.showReasoningButton} onClick={handleExpand}>
						<span>Reasoning</span>
						<FontAwesomeIcon icon={faAngleRight} className={styles.reasoningIcon} />
					</button>
				</div>
			)}
		</div>
	);
}

export function AskFellowPanelInner({
	conversation,
	onSelectedConversation,
	inModal,
}: {
	conversation: AskCopilotConversation;
	onSelectedConversation?: (conversationId: string) => void;
	inModal?: boolean;
}) {
	const { messages = [], suggestedQuestions = [] } = conversation ?? {};
	const latestMessage = messages?.[messages.length - 1];
	const pendingMessage = latestMessage?.pending ? latestMessage : null;
	const latestUserMessageHasResponse = (() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const message = messages[i];
			if (message.role === "USER" && !isToolUseOrResultMessage(message)) {
				return false;
			} else if (message.role === "ASSISTANT" && message.isResponse && !isToolUseOrResultMessage(message)) {
				return true;
			}
		}
		return true;
	})();

	const messageContainerRef = React.useRef<HTMLDivElement>(null);
	const [showScrollButton, setShowScrollButton] = React.useState(false);

	// Check if we should show the scroll button
	const checkScrollPosition = React.useCallback(() => {
		const messageContainer = messageContainerRef.current;
		if (!messageContainer) return;

		// Only show the button if:
		// 1. There's actually scrollable content (scrollHeight > clientHeight)
		// 2. The user isn't already at the bottom
		const hasScrollableContent = messageContainer.scrollHeight > messageContainer.clientHeight;
		const isAtBottom = messageContainer.scrollTop >= messageContainer.scrollHeight - messageContainer.clientHeight - 20;

		setShowScrollButton(hasScrollableContent && !isAtBottom);
	}, []);

	const scrollToBottomSmooth = React.useCallback(() => {
		const messageContainer = messageContainerRef.current;
		if (messageContainer) {
			// Use smooth scrolling when user clicks the button
			requestAnimationFrame(() => {
				messageContainer.scrollTo({
					top: messageContainer.scrollHeight,
					behavior: "smooth",
				});
				setShowScrollButton(false);
			});
		}
	}, []);

	React.useEffect(() => {
		const messageContainer = messageContainerRef.current;
		if (!messageContainer) return;

		function onScroll() {
			if (!messageContainer) return;
			const hasScrollableContent = messageContainer.scrollHeight > messageContainer.clientHeight;
			const isAtBottom =
				messageContainer.scrollTop >= messageContainer.scrollHeight - messageContainer.clientHeight - 20;
			setShowScrollButton(hasScrollableContent && !isAtBottom);
		}

		messageContainer.addEventListener("scroll", onScroll);
		onScroll();

		return () => {
			messageContainer.removeEventListener("scroll", onScroll);
		};
	}, []);

	React.useEffect(() => {
		if (onSelectedConversation && conversation?.id && conversation.messages.length > 0) {
			onSelectedConversation(conversation?.id);
		}
	}, [onSelectedConversation, conversation?.id, conversation.messages.length]);

	React.useEffect(() => {
		const messagesLength = conversation?.messages?.length;
		if (
			messagesLength &&
			conversation.messages[messagesLength - 1].role === "USER" &&
			!isToolUseOrResultMessage(conversation.messages[messagesLength - 1])
		) {
			scrollToBottomSmooth();
		}
	}, [conversation?.messages?.length, conversation.messages, scrollToBottomSmooth]);

	React.useEffect(() => {
		const messageContainer = messageContainerRef.current;
		if (!messageContainer) return;

		function onResize() {
			checkScrollPosition();
		}

		const observer = new MutationObserver(() => {
			checkScrollPosition();
		});

		messageContainer.addEventListener("scroll", checkScrollPosition);
		window.addEventListener("resize", onResize);
		observer.observe(messageContainer, {
			childList: true,
			subtree: true,
			characterData: true,
		});

		checkScrollPosition();

		return () => {
			messageContainer.removeEventListener("scroll", checkScrollPosition);
			window.removeEventListener("resize", onResize);
			observer.disconnect();
		};
	}, [conversation?.messages?.length, checkScrollPosition]);

	useAskFellowPanelConversationListener(conversation, checkScrollPosition);

	const [sendMessage] = useAskCopilotSendMessageViaConversation(conversation, false);

	const loadingIndicatorText = React.useMemo(() => {
		const defaultText = "Gathering context";

		if (!messages || messages.length === 0) {
			return null;
		}

		const lastMessage = messages[messages.length - 1];

		if (lastMessage.role === "USER" && !lastMessage.contentJson) {
			return defaultText;
		}

		return null;
	}, [messages]);

	const onStopGenerating = useStopGenerating(latestMessage?.id);

	const aiFeaturesDisabled = !React.useContext(AppContext).account?.allowAiFeatures;

	const currentlyGenerating = Boolean(pendingMessage || (!pendingMessage && !latestUserMessageHasResponse));

	const currentlyGeneratingTools = React.useMemo(() => {
		const isLastAssistantMessage =
			messages.length > 0 &&
			messages[messages.length - 1].role === "ASSISTANT" &&
			messages[messages.length - 1].content.includes("<result>");
		return !isLastAssistantMessage && currentlyGenerating;
	}, [messages, currentlyGenerating]);

	const contextFooterRef = React.useRef<HTMLDivElement>(null);

	if (!messages || !suggestedQuestions) return null;

	const toolUses = groupToolUsesWithResponse(messages);

	return (
		<>
			<div ref={messageContainerRef} className={styles.messagesContainerWrapper}>
				<p className={styles.conversationSubtitle}>This is a private conversation between you and Fellow</p>
				{toolUses.map((group, i) => {
					const currentlyGeneratingLatestTool = toolUses.length - 1 === i && currentlyGeneratingTools;
					if (group.type === "toolUsesAndResults") {
						return (
							<>
								<ToolsUsed
									currentlyGenerating={currentlyGeneratingLatestTool}
									key={`${i}-tools-used`} // eslint-disable-line react/no-array-index-key
									tools={group.usesAndResults}
								/>
							</>
						);
					}
					const { message } = group;
					if (!message) return null;
					const messageData = readInlineData(GroupToolUsesWithResponseMessageFragment, message);
					return (
						<>
							{messageData.role === "USER" && i > 0 && <div className={styles.messageSpacer} />}
							<ChatMessage
								key={messageData.id}
								conversation={conversation}
								message={message as unknown as AskFellowPanelInner_ChatMessage_message$key}
								hideButtons={i === 0 || !!messageData.contentJson}
							/>
						</>
					);
				})}
				{loadingIndicatorText && (
					<div className={styles.toolsUsedContainer}>
						{
							<div className={styles.reasoningContainerWithBorder}>
								<div className={styles.showReasoningButton}>
									<span>Reasoning</span>
								</div>
								<div className={styles.loadingIndicatorWrapper}>
									<span className={styles.loadingIndicator}>{loadingIndicatorText}</span>
									<span className={styles.loadingShimmerBar}></span>
								</div>
							</div>
						}
					</div>
				)}
			</div>
			<div
				className={cx(styles.messageFormFooter, {
					[styles.inModal]: inModal,
					[styles.atBottom]: !showScrollButton,
				})}
			>
				{showScrollButton && (
					<div
						className={styles.scrollToBottomButton}
						onClick={scrollToBottomSmooth}
						role="button"
						aria-label="Scroll to bottom"
						tabIndex={0}
					>
						<ScrollToBottomIcon />
					</div>
				)}
				<MessageForm
					conversation={conversation}
					onSubmit={sendMessage}
					currentlyGenerating={currentlyGenerating}
					canStopGenerating={currentlyGenerating}
					onStop={onStopGenerating}
					reachedMessageLimit={conversation?.reachedMessageLimit ?? false}
					disabled={aiFeaturesDisabled}
					showAIDisclaimer={true}
					supportPreFilledQuery={false}
					placeholderText="Follow up or ask something new"
					footer={
						<ContextPill
							conversationKey={conversation}
							className={styles.contextPill}
							contextFooterRef={contextFooterRef}
						/>
					}
					contextFooterRef={contextFooterRef}
				/>
			</div>
		</>
	);
}

export function MessageForm({
	onSubmit,
	disabled,
	canStopGenerating,
	onStop,
	currentlyGenerating,
	reachedMessageLimit,
	conversation,
	showAIDisclaimer,
	supportPreFilledQuery,
	placeholderText,
	footer,
	contextFooterRef,
}: {
	onSubmit: (content: string) => void;
	disabled?: boolean;
	canStopGenerating: boolean;
	onStop: () => void;
	currentlyGenerating: boolean;
	reachedMessageLimit: boolean;
	conversation: AskCopilotConversation;
	showAIDisclaimer: boolean;
	supportPreFilledQuery: boolean;
	placeholderText?: string;
	footer?: React.ReactNode;
	contextFooterRef?: React.RefObject<HTMLDivElement>;
}) {
	const conversationId = conversation?.id ?? "";
	const [messageContent, setMessageContent] = React.useState("");
	const [isStoppingGeneration, setIsStoppingGeneration] = React.useState(false);

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const messageContentRef = React.useRef(messageContent);
	messageContentRef.current = messageContent;

	const messageIsEmpty = !messageContent.trim();
	const onMessageFormSubmit = React.useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (currentlyGenerating || messageIsEmpty) return;
			onSubmit(messageContentRef.current);
			setMessageContent("");
			setIsStoppingGeneration(false); // Reset stopping state when submitting a new message
		},
		[onSubmit, currentlyGenerating, messageIsEmpty],
	);

	const onMessageContentChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newMessageContent = e.target.value;
		setMessageContent(newMessageContent);
		setIsStoppingGeneration(false); // Reset stopping state when typing a new message
	}, []);

	// Handle the stop generation click
	const handleStopGeneration = React.useCallback(() => {
		setIsStoppingGeneration(true);
		dispatchAnalyticsTrack("[Ask Copilot] Clicked stop generating");
		onStop();
	}, [onStop, dispatchAnalyticsTrack]);

	// Reset the stopping state when canStopGenerating changes to false
	React.useEffect(() => {
		if (!canStopGenerating) {
			setIsStoppingGeneration(false);
		}
	}, [canStopGenerating]);

	const onSearchQuery = React.useCallback(
		(query: string) => {
			if (supportPreFilledQuery && query) {
				if (checkSentenceIsQuestion(query)) {
					onSubmit(query);
				} else {
					setMessageContent(query);
				}
			}
		},
		[onSubmit, supportPreFilledQuery],
	);

	React.useEffect(() => {
		const searchQuery = localStorage.getItem("ask-fellow-search-query");
		if (searchQuery) {
			onSearchQuery(searchQuery);
		}
		localStorage.removeItem("ask-fellow-search-query");

		// Clear the search query from local storage when the component unmounts
		// This is necessary in case the panel is already open when the search-query is set
		// so we guarantee the search-query is deleted from local storage
		return () => {
			localStorage.removeItem("ask-fellow-search-query");
		};
	}, [onSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		askFellowSearchQueryEmitter.on("ask-fellow-search-query", onSearchQuery);
		return () => {
			askFellowSearchQueryEmitter.removeAllListeners("ask-fellow-search-query");
		};
	}, [onSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

	const virtualKeyboardDetector = React.useMemo(() => new VirtualKeyboardDetector(), []);
	React.useEffect(() => {
		window.addEventListener("keydown", virtualKeyboardDetector.onKeyDown);
		window.addEventListener("keyup", virtualKeyboardDetector.onKeyUp);

		return () => {
			window.removeEventListener("keydown", virtualKeyboardDetector.onKeyDown);
			window.removeEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		};
	}, [virtualKeyboardDetector]);

	const textareaId = useID();

	// All this is so we can only show the outline around the input if the user
	// tabbed in (same as Discord). Normally for this purpose one would use
	// :focus-visible, but that will show the outline on the textarea whenever
	// it's focused, regardless of how focus got there.
	const [focusedViaTab, setFocusedViaTab] = React.useState(false);
	React.useEffect(() => {
		function onKeyUp(e: KeyboardEvent) {
			if (e.key === "Tab" && document.querySelector(`#${textareaId}:focus`)) {
				setFocusedViaTab(true);
			}
		}

		window.addEventListener("keyup", onKeyUp);
		return () => {
			window.removeEventListener("keyup", onKeyUp);
		};
	}, [textareaId]);

	const onTextAreaBlur = React.useCallback(() => {
		setFocusedViaTab(false);
	}, []);

	const formRef = React.useRef<HTMLFormElement>(null);
	const onTextAreaKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey && !virtualKeyboardDetector.isVirtualKeyboard()) {
				e.preventDefault();
				formRef.current?.requestSubmit();
			}
		},
		[virtualKeyboardDetector],
	);

	const defaultPlaceholder = useAskFellowTypingAnimation();

	let placeholder;
	let ariaPlaceholder;
	if (!React.useContext(AppContext).account?.allowAiFeatures) {
		placeholder = "Your workspace admin has disabled AI features";
	} else if (reachedMessageLimit) {
		placeholder = "You have asked the maximum number of questions";
	} else {
		placeholder = placeholderText ?? defaultPlaceholder;
		ariaPlaceholder = "What were the key decisions made?";
	}

	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

	React.useEffect(() => {
		if (textAreaRef.current) {
			textAreaRef.current.focus();
			let end = textAreaRef.current.value.length;
			textAreaRef.current.setSelectionRange(end, end);
		}
	}, [conversationId, conversation?.context]);

	const sendButtonDisabled = disabled || messageIsEmpty || currentlyGenerating;

	const hasFooter = !!contextFooterRef?.current;

	const form = (
		<form
			ref={formRef}
			onSubmit={onMessageFormSubmit}
			className={cx(styles.messageForm, { [styles.focusedViaTab]: focusedViaTab })}
		>
			<TextAreaAutoSize
				id={textareaId}
				ref={textAreaRef}
				className={styles.messageInput}
				aria-placeholder={ariaPlaceholder ?? placeholder}
				placeholder={placeholder}
				value={messageContent}
				onChange={onMessageContentChange}
				onBlur={onTextAreaBlur}
				onKeyDown={onTextAreaKeyDown}
				minRows={3}
				maxRows={hasFooter ? 12 : 14}
				disabled={disabled || reachedMessageLimit}
			/>
			<div className={styles.messageFormButtonContainer}>
				{!canStopGenerating ? (
					<Button type="submit" disabled={sendButtonDisabled} className={styles.sendButton}>
						{sendButtonDisabled ? <SendButtonInactive /> : <SendButtonActive />}
					</Button>
				) : (
					<Button
						type="button"
						onClick={handleStopGeneration}
						className={styles.sendButton}
						disabled={isStoppingGeneration}
					>
						<StopGeneratingButton />
					</Button>
				)}
			</div>
		</form>
	);

	return (
		<>
			<div className={styles.messageInputContainer}>
				<div className={cx(styles.messageInputInner, { [styles.withFooter]: hasFooter })}>{form}</div>
				{footer}
			</div>
			{showAIDisclaimer && <p className={styles.aiDisclaimerBottom}>AI outputs can be wrong. Check important info.</p>}
		</>
	);
}
